import React from 'react';
import { motion } from 'framer-motion';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Logo({
  className = '',
  size = 'md'
}: {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizes = {
    sm: 'text-xl',
    md: 'text-2xl',
    lg: 'text-3xl'
  };
  const plusSizes = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl'
  };
  const markSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };
  return <div className={`flex items-center gap-2 ${className}`} data-id="element-6790">
      {/* Logo mark — circular ring inspired by the original brand mark */}
      <div className={`relative ${markSizes[size]} shrink-0`} data-id="element-6791">
        <svg viewBox="0 0 32 32" className="w-full h-full" fill="none" data-id="element-6792">
          <circle cx="16" cy="16" r="13" stroke="#0B1B3B" strokeWidth="4" strokeLinecap="round" strokeDasharray="55 25" transform="rotate(-90 16 16)" data-id="element-6793" />
          <circle cx="16" cy="16" r="13" stroke="#0EA5A4" strokeWidth="4" strokeLinecap="round" strokeDasharray="20 80" transform="rotate(-90 16 16)" data-id="element-6794" />
          <circle cx="16" cy="3" r="2" fill="#FF6B4A" data-id="element-6795" />
        </svg>
      </div>
      {/* Wordmark */}
      <div className={`font-bold ${sizes[size]} tracking-tight flex items-start leading-none`} data-id="element-6796">
        <span className="text-brand-navy" data-id="element-6797">Offsite</span>
        <span className={`text-brand-coral ${plusSizes[size]} font-black relative -top-0.5 ml-0.5`} data-id="element-6798">
          +
        </span>
      </div>
    </div>;
}
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  href?: string;
  children: React.ReactNode;
}
export function Button({
  variant = 'primary',
  size = 'md',
  isLoading,
  className = '',
  href,
  children,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed no-underline';
  const variants = {
    primary: 'bg-brand-navy text-white hover:bg-slate-800 focus:ring-brand-navy shadow-sm',
    secondary: 'bg-brand-teal text-white hover:bg-teal-500 focus:ring-brand-teal shadow-sm',
    accent: 'bg-brand-coral text-white hover:bg-orange-500 focus:ring-brand-coral shadow-sm',
    outline: 'border-2 border-brand-navy text-brand-navy hover:bg-slate-50 focus:ring-brand-navy',
    ghost: 'text-slate-600 hover:text-brand-navy hover:bg-slate-100 focus:ring-slate-200'
  };
  const sizes = {
    sm: 'text-sm px-4 py-2',
    md: 'text-sm px-6 py-2.5',
    lg: 'text-base px-8 py-3.5'
  };
  const combinedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;
  if (href) {
    return <Link to={href} className={combinedClassName} data-id="element-6799">
        {isLoading && <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-6800" />}
        {children}
      </Link>;
  }
  return <button className={combinedClassName} disabled={isLoading || props.disabled} {...props} data-id="element-6801">
      {isLoading && <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-6802" />}
      {children}
    </button>;
}
export function Section({
  children,
  className = '',
  id
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return <section id={id} className={`py-20 md:py-32 ${className}`} data-id="element-6803">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-6804">{children}</div>
    </section>;
}
export function FadeIn({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.6,
    delay,
    ease: [0.21, 0.47, 0.32, 0.98]
  }} className={className} data-id="element-6805">
      {children}
    </motion.div>;
}